import { useCallback, useState } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import {
  BANNER_LANGUAGES,
  BANNER_MEDIA_KIND,
  BANNER_TRANSLATION_LANGUAGES,
  type BannerFormValues,
  type BannerLanguage,
  type BannerMediaKind,
  type BannerTranslationLanguage,
} from '../types/bannerForm';
import type { BannerVideoMetadata } from '../utils/bannerMedia';

/** Marca todo `setValue` como alteração do operador, para o aviso de descarte. */
const DIRTY = { shouldDirty: true } as const;

export interface UseBannerMediaFieldsParams {
  readonly setValue: UseFormSetValue<BannerFormValues>;
  /** Idioma selecionado na barra de idioma: define em quais campos a mídia entra. */
  readonly language: BannerLanguage;
}

/**
 * Campos de mídia do formulário de Banner: tipo (imagem/vídeo), arte e MP4, no
 * idioma selecionado.
 *
 * Os arquivos moram nos **valores do formulário** (React Hook Form) — o que este
 * hook guarda em estado local são só os metadados lidos do MP4 no browser
 * (duração e resolução), que não vêm da API nem são enviados como campo próprio,
 * e existem por idioma porque cada aba pode ter o seu arquivo.
 *
 * Duas regras atravessam tudo: um banner é **imagem ou vídeo, nunca os dois**, e o
 * cadastro **não remove mídia — só substitui**. Por isso o descarte é amarrado à
 * escolha de um arquivo, não à troca de aba: alternar Imagem ⇄ Vídeo preserva o que
 * já estava escolhido, e escolher um arquivo novo apaga o do outro tipo (em todos os
 * idiomas). O que já está no cadastro sai de lá apenas quando um arquivo novo é
 * enviado no lugar — exigido por `bannerFormSchema`.
 *
 * @example
 * ```tsx
 * const media = useBannerMediaFields({ setValue, language });
 * <BannerMediaSection onMediaKindChange={media.handleMediaKindChange} … />
 * ```
 */
export function useBannerMediaFields({ setValue, language }: UseBannerMediaFieldsParams) {
  const [videoMetadataByLanguage, setVideoMetadataByLanguage] = useState<
    Partial<Record<BannerLanguage, BannerVideoMetadata | null>>
  >({});

  const translationKey =
    language === BANNER_LANGUAGES.PtBr ? null : (language as BannerTranslationLanguage);

  /** Esquece os metadados lidos dos MP4 — usado ao (re)carregar um banner. */
  const resetVideoMetadata = useCallback(() => setVideoMetadataByLanguage({}), []);

  /**
   * Troca a aba de mídia **sem descartar nada**: alternar Imagem ⇄ Vídeo só muda
   * qual campo a tela usa, e o que já estava escolhido continua lá. O descarte
   * acontece quando entra um arquivo novo — é ele que substitui a mídia.
   */
  const handleMediaKindChange = useCallback(
    (kind: BannerMediaKind) => setValue('mediaKind', kind, DIRTY),
    [setValue],
  );

  /**
   * Apaga do formulário **todo o conteúdo** do tipo que acabou de ser substituído:
   * o arquivo escolhido e o caminho da mídia que estava no cadastro, em todos os
   * idiomas. Colocar um vídeo zera a imagem, e vice-versa.
   */
  const dropOtherKind = useCallback(
    (kept: BannerMediaKind) => {
      const keptVideo = kept === BANNER_MEDIA_KIND.Video;

      if (keptVideo) {
        setValue('imageFile', null, DIRTY);
        setValue('existingImageUrl', undefined, DIRTY);
        setValue('imageDesktopFile', null, DIRTY);
        setValue('existingImageDesktopUrl', undefined, DIRTY);
      } else {
        setValue('videoFile', null, DIRTY);
        setValue('videoDurationSeconds', null, DIRTY);
        setValue('existingVideoUrl', undefined, DIRTY);
        resetVideoMetadata();
      }

      // A tradução sobe o mesmo tipo de mídia do cadastro em português: o conteúdo
      // do outro tipo sai das abas de idioma também, senão o save das traduções
      // enviaria a mídia que o operador acabou de substituir.
      for (const lang of BANNER_TRANSLATION_LANGUAGES) {
        setValue(`translations.${lang}.${keptVideo ? 'imageFile' : 'videoFile'}`, null, DIRTY);
        setValue(
          `translations.${lang}.${keptVideo ? 'existingImageUrl' : 'existingVideoUrl'}`,
          undefined,
          DIRTY,
        );
        if (keptVideo) {
          setValue(`translations.${lang}.imageDesktopFile`, null, DIRTY);
          setValue(`translations.${lang}.existingImageDesktopUrl`, undefined, DIRTY);
        }
      }
    },
    [resetVideoMetadata, setValue],
  );

  const handleVideoChange = useCallback(
    (metadata: BannerVideoMetadata | null) => {
      setVideoMetadataByLanguage(current => ({ ...current, [language]: metadata }));

      if (translationKey) {
        setValue(`translations.${translationKey}.videoFile`, metadata?.file ?? null, DIRTY);
        if (metadata) {
          setValue(`translations.${translationKey}.imageFile`, null, DIRTY);
          setValue(`translations.${translationKey}.existingImageUrl`, undefined, DIRTY);
        }
        return;
      }

      setValue('videoFile', metadata?.file ?? null, DIRTY);
      setValue('videoDurationSeconds', metadata?.durationSeconds ?? null, DIRTY);
      if (metadata) dropOtherKind(BANNER_MEDIA_KIND.Video);
    },
    [dropOtherKind, language, setValue, translationKey],
  );

  const handleImageChange = useCallback(
    (file: File | null) => {
      if (translationKey) {
        setValue(`translations.${translationKey}.imageFile`, file, DIRTY);
        if (file) {
          setValue(`translations.${translationKey}.videoFile`, null, DIRTY);
          setValue(`translations.${translationKey}.existingVideoUrl`, undefined, DIRTY);
        }
        return;
      }
      setValue('imageFile', file, DIRTY);
      if (file) dropOtherKind(BANNER_MEDIA_KIND.Image);
    },
    [dropOtherKind, setValue, translationKey],
  );

  /** Arte do par legado: mesma regra do mobile, sem reflexo no preview. */
  const handleImageDesktopChange = useCallback(
    (file: File | null) => {
      if (translationKey) {
        setValue(`translations.${translationKey}.imageDesktopFile`, file, DIRTY);
        if (file) {
          setValue(`translations.${translationKey}.videoFile`, null, DIRTY);
          setValue(`translations.${translationKey}.existingVideoUrl`, undefined, DIRTY);
        }
        return;
      }
      setValue('imageDesktopFile', file, DIRTY);
      if (file) dropOtherKind(BANNER_MEDIA_KIND.Image);
    },
    [dropOtherKind, setValue, translationKey],
  );

  return {
    /** Metadados do MP4 do cadastro em português. */
    videoMetadata: videoMetadataByLanguage[BANNER_LANGUAGES.PtBr] ?? null,
    /** Metadados do MP4 do idioma selecionado (`null` em português). */
    translationVideoMetadata: translationKey
      ? (videoMetadataByLanguage[translationKey] ?? null)
      : null,
    resetVideoMetadata,
    handleMediaKindChange,
    handleVideoChange,
    handleImageChange,
    handleImageDesktopChange,
  } as const;
}
